import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useApi, errMsg, fmtPrice } from "@/lib/api";
import { usePageTitle } from "@/lib/site";

const Info = ({ k, v, testId }) => (
  <div className="flex justify-between py-2 border-b border-[var(--line)] text-sm" data-testid={testId}><span className="text-[var(--muted-ink)]">{k}</span><span className="font-semibold" dir="auto">{v}</span></div>
);

export default function PaymentResult() {
  usePageTitle("نتيجة الدفع");
  const [sp] = useSearchParams();
  const ref = sp.get("ref");
  const cancelled = sp.get("cancelled") === "1" || sp.get("success") === "false";
  const api = useApi();
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const check = useCallback(async (attempt = 0) => {
    try {
      const r = await api("get", `/payments/${ref}/verify`, null, { params: { cancelled } });
      setRes(r);
      if (r.status === "initiated" && attempt < 5) setTimeout(() => check(attempt + 1), 3000);
    } catch (e) {
      setRes({ status: "error", error: errMsg(e) });
    }
  }, [api, ref, cancelled]);
  useEffect(() => {
    check();
  }, [check]);
  const retry = async () => {
    setBusy(true);
    try {
      const r = await api("post", `/enrollments/${res.enrollment.id}/retry`);
      window.location.href = r.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  const st = res?.status;
  return (
    <div className="container-x py-16 md:py-24 max-w-2xl" data-testid="payment-result-page">
      <div className="card-lux !transform-none p-8 md:p-10 text-center">
        {!res || st === "initiated" ? (
          <div data-testid="payment-status-pending"><Loader2 className="mx-auto animate-spin text-[var(--copper)]" size={56} /><h1 className="font-display text-2xl font-bold mt-5">جارٍ التحقق من عملية الدفع...</h1><p className="text-[var(--muted-ink)] mt-2">يتم التحقق من الدفع مباشرة من بوابة الدفع.</p></div>
        ) : st === "paid" ? (
          <div data-testid="payment-status-success"><CheckCircle2 className="mx-auto text-emerald-600" size={64} /><h1 className="font-display text-3xl font-extrabold mt-5">{res?.payment?.provider === "coupon" ? "تم تأكيد تسجيلك مجاناً بالقسيمة" : "تم الدفع وتأكيد تسجيلك بنجاح"}</h1><p className="text-[var(--muted-ink)] mt-2">أرسلنا لك رسالة تأكيد على بريدك الإلكتروني.</p></div>
        ) : st === "error" ? (
          <div data-testid="payment-status-error"><XCircle className="mx-auto text-red-600" size={64} /><h1 className="font-display text-2xl font-bold mt-5">{res.error}</h1></div>
        ) : (
          <div data-testid="payment-status-failed"><XCircle className="mx-auto text-red-600" size={64} /><h1 className="font-display text-3xl font-extrabold mt-5">لم تكتمل عملية الدفع</h1><p className="text-[var(--muted-ink)] mt-2">لم يتم خصم أي مبلغ. يمكنك إعادة المحاولة الآن.</p></div>
        )}
        {res?.payment && (
          <div className="mt-8 text-right">
            <Info k="رقم التسجيل" v={res.payment.reg_no} testId="result-reg-no" />
            <Info k="الرقم المرجعي للمعاملة" v={res.payment.ref} testId="result-ref" />
            <Info k="الدورة" v={res.payment.course_title} />
            <Info k="المبلغ" v={fmtPrice(res.payment.amount)} testId="result-amount" />
          </div>
        )}
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          {(st === "failed" || st === "expired") && res.enrollment?.status !== "confirmed" && (
            <button onClick={retry} disabled={busy} className="btn-copper" data-testid="payment-retry-btn">{busy ? "جارٍ التحويل..." : "إعادة محاولة الدفع"}</button>
          )}
          <Link to="/account" className="btn-gold-outline" data-testid="payment-go-account-btn">الذهاب إلى حسابي</Link>
        </div>
      </div>
    </div>
  );
}
